import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

const AdminStatsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [facultyStats, setFacultyStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);

  const ADMIN_EMAIL = 'petre-alexandru.dumitrescu0721@stud.umfcd.ro';

  // Security check
  useEffect(() => {
    if (!user || user.isGuest || user.email !== ADMIN_EMAIL) {
      navigate('/calculator', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const loadAdminStats = async () => {
      try {
        // Get overall statistics
        const [usersSnapshot, calculationsSnapshot, latestAveragesSnapshot] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'calculations')),
          getDocs(collection(db, 'latestAverages'))
        ]);

        // Process user statistics
        const users = [];
        const usersByFaculty = {};
        usersSnapshot.forEach(doc => {
          const userData = doc.data();
          users.push(userData);
          
          // Count by faculty (if available)
          if (userData.faculty) {
            usersByFaculty[userData.faculty] = (usersByFaculty[userData.faculty] || 0) + 1;
          }
        });

        // Process calculations
        const calculations = [];
        const calculationsByFaculty = {};
        const calculationsByDate = {};
        calculationsSnapshot.forEach(doc => {
          const calcData = doc.data();
          calculations.push(calcData);
          
          // Count by faculty
          calculationsByFaculty[calcData.faculty] = (calculationsByFaculty[calcData.faculty] || 0) + 1;
          
          // Count by date
          const date = calcData.date?.toDate?.() || new Date(calcData.date);
          const dateKey = date.toDateString();
          calculationsByDate[dateKey] = (calculationsByDate[dateKey] || 0) + 1;
        });

        // Process latest averages for rankings
        const averages = [];
        const averagesByFaculty = {};
        latestAveragesSnapshot.forEach(doc => {
          const avgData = doc.data();
          averages.push(avgData);
          
          const key = `${avgData.university}-${avgData.year}`;
          if (!averagesByFaculty[key]) {
            averagesByFaculty[key] = [];
          }
          averagesByFaculty[key].push(avgData.average);
        });

        // Calculate faculty statistics
        const facultyStatsData = {};
        Object.keys(averagesByFaculty).forEach(key => {
          const [faculty, year] = key.split('-');
          const facultyAverages = averagesByFaculty[key];
          
          const avg = facultyAverages.reduce((sum, val) => sum + val, 0) / facultyAverages.length;
          const max = Math.max(...facultyAverages);
          const min = Math.min(...facultyAverages);
          
          facultyStatsData[key] = {
            faculty,
            year: parseInt(year),
            students: facultyAverages.length,
            averageGPA: avg.toFixed(2),
            highestGPA: max.toFixed(2),
            lowestGPA: min.toFixed(2),
            calculations: calculationsByFaculty[faculty] || 0
          };
        });

        // Get recent activity
        const recentCalcsQuery = query(
          collection(db, 'calculations'),
          orderBy('date', 'desc'),
          limit(10)
        );
        const recentCalcsSnapshot = await getDocs(recentCalcsQuery);
        const recentCalcs = [];
        recentCalcsSnapshot.forEach(doc => {
          recentCalcs.push(doc.data());
        });

        setStats({
          totalUsers: users.length,
          totalCalculations: calculations.length,
          totalUniqueAverages: averages.length,
          usersByFaculty,
          calculationsByFaculty,
          calculationsByDate,
          averagesByFaculty
        });

        setFacultyStats(facultyStatsData);
        setRecentActivity(recentCalcs);

      } catch (error) {
        console.error('Error loading admin stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user && !user.isGuest && user.email === ADMIN_EMAIL) {
      loadAdminStats();
    }
  }, [user]);

  const formatDate = (dateValue) => {
    const date = dateValue?.toDate?.() || new Date(dateValue);
    return date.toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Don't render if not admin
  if (!user || user.isGuest || user.email !== ADMIN_EMAIL) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full mx-auto mb-4"></div>
          <p className="text-blue-600 font-medium">Loading admin statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-xl">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/calculator')}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Calculator</span>
            </button>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/cleanup')}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
              >
                <span>🔧</span>
                <span>Database Cleanup</span>
              </button>
              <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                👑 Admin Panel
              </div>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">👑</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Calculator UMFCD - Overall Statistics & Analytics</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-3xl font-bold text-blue-600">{stats?.totalUsers || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">👥</span>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Calculations</p>
                <p className="text-3xl font-bold text-green-600">{stats?.totalCalculations || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🧮</span>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Rankings</p>
                <p className="text-3xl font-bold text-purple-600">{stats?.totalUniqueAverages || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🏆</span>
              </div>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Faculty Programs</p>
                <p className="text-3xl font-bold text-orange-600">{Object.keys(facultyStats).length}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🏫</span>
              </div>
            </div>
          </div>
        </div>

        {/* Faculty Statistics */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-white/20 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">📊</span>
            Faculty Statistics
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="pb-3 text-gray-600 font-semibold">Faculty</th>
                  <th className="pb-3 text-gray-600 font-semibold">Year</th>
                  <th className="pb-3 text-gray-600 font-semibold">Students</th>
                  <th className="pb-3 text-gray-600 font-semibold">Avg GPA</th>
                  <th className="pb-3 text-gray-600 font-semibold">Highest</th>
                  <th className="pb-3 text-gray-600 font-semibold">Lowest</th>
                  <th className="pb-3 text-gray-600 font-semibold">Calculations</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(facultyStats).map((stat, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 font-medium text-blue-900">{stat.faculty}</td>
                    <td className="py-3 text-gray-700">Anul {stat.year}</td>
                    <td className="py-3 text-gray-700">{stat.students}</td>
                    <td className="py-3 font-semibold text-green-600">{stat.averageGPA}</td>
                    <td className="py-3 font-semibold text-emerald-600">{stat.highestGPA}</td>
                    <td className="py-3 font-semibold text-orange-600">{stat.lowestGPA}</td>
                    <td className="py-3 text-gray-700">{stat.calculations}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-white/20">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">⚡</span>
            Recent Activity
          </h2>
          
          <div className="space-y-4">
            {recentActivity.map((activity, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg">🧮</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {activity.faculty} - Anul {activity.year}
                    </p>
                    <p className="text-sm text-gray-600">
                      Media: {activity.average} | User: {activity.userId?.substring(0, 8)}...
                    </p>
                  </div>
                </div>
                <div className="text-right text-sm text-gray-500">
                  {formatDate(activity.date)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="mt-8 bg-green-50 border-2 border-green-200 rounded-2xl p-6">
          <h3 className="font-bold text-green-800 mb-3 flex items-center">
            <span className="mr-2">💚</span>
            System Health
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-green-700">Database operational</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-green-700">Rankings updated</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-green-700">No duplicate entries detected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStatsPage;
